/**
 * useNoteDoc — React hook that mounts a per-note Y.Doc for the current
 * spawn context, with refcount lifecycle and reactive sync state.
 *
 * Two flavors:
 *   - {@link useNoteDoc}(deckId, noteId): personal scope (user-do +
 *     userId), reads everything from {@link useNoteDocSpawnConfig}.
 *   - {@link useGroupNoteDoc}(groupId, deckId, noteId): group scope
 *     (study-group-do + groupId). Reuses host/getToken from the
 *     personal spawn config — the auth token authenticates the user
 *     for both UserDo and StudyGroupDo, no separate token needed.
 *
 * On mount the hook acquires a NoteDoc from the global
 * {@link noteDocRegistry}; on unmount it releases — the registry's
 * grace window keeps the doc alive briefly in case the user navigates
 * back. Returns `null` doc/fragment when no spawn config is available
 * (signed out / pre-hydration / outside a provider), or when no note id is
 * given — the way a caller that needs no note acquires none (a hook can't be
 * called conditionally).
 */

import { useEffect, useRef, useState } from 'react';
import type * as Y from 'yjs';

import type { NoteDoc, NoteDocError } from './note-doc';
import { noteDocRegistry } from './note-doc-registry';
import { useNoteDocSpawnConfig } from './note-doc-spawn-context';

export interface UseNoteDocResult {
  /** The per-note Y.Doc wrapper. `null` until the spawn context is ready. */
  doc: NoteDoc | null;
  /** Reactive tiptap fragment. `null` if the note has a different kind,
   *  if no doc has been loaded yet, or if no content has been written. */
  fragment: Y.XmlFragment | null;
  /** True once IDB has replayed cached state into the Y.Doc. */
  idbSynced: boolean;
  /** True once the WebSocket Yjs sync handshake has completed. */
  wsSynced: boolean;
  /** True when it's safe to auto-create empty Y types. See `notes-view.tsx`. */
  synced: boolean;
  /** Most recent connection / sync error, or `null` if everything is
   *  healthy. Cleared automatically on the next successful sync. */
  error: NoteDocError | null;
}

const EMPTY: UseNoteDocResult = {
  doc: null,
  fragment: null,
  idbSynced: false,
  wsSynced: false,
  synced: false,
  error: null,
};

interface UseNoteDocInternalArgs {
  party: string | null;
  room: string | null;
  host: string | null;
  getToken: (() => Promise<string | null>) | undefined;
  deckId: string;
  /** Absent: acquire nothing. */
  noteId: string | undefined;
}

/** Identity of WHICH doc is asked for; `\x01` cannot occur in any part. */
const noteTargetKey = (a: UseNoteDocInternalArgs): string =>
  [a.party ?? '', a.room ?? '', a.deckId, a.noteId ?? ''].join('\x01');

function useNoteDocInternal(args: UseNoteDocInternalArgs): UseNoteDocResult {
  // Capture the latest token resolver in a ref — it changes identity
  // freely upstream (e.g. closures over auth session state) but we
  // don't want every change to tear/rebuild WS connections.
  const getTokenRef = useRef(args.getToken);
  getTokenRef.current = args.getToken;
  const hostRef = useRef(args.host);
  hostRef.current = args.host;

  // The acquired doc TOGETHER with the target it was acquired for. The
  // acquire happens in an effect, so the render right after the target
  // changes (the user switches from one note to another) still holds the
  // previous note's doc: returning it would hand a consumer the OLD note's
  // fragment under the NEW note's id — and an editor binds to the fragment
  // it is given for life, so it would keep writing the old note while
  // registered as the new one. A doc for another target is never returned;
  // the caller sees "not loaded" for that one render instead.
  const [acquired, setAcquired] = useState<{ key: string; doc: NoteDoc } | null>(null);
  // Counter forces re-render on every doc change (sync flag + Y.Doc
  // updates). The actual values are read from `doc` directly below.
  const [, forceRender] = useState(0);

  const targetKey = noteTargetKey(args);

  useEffect(() => {
    const host = hostRef.current;
    if (!args.party || !args.room || !host || !args.noteId) {
      setAcquired(null);
      return;
    }
    const handle = noteDocRegistry.acquire({
      party: args.party,
      room: args.room,
      deckId: args.deckId,
      noteId: args.noteId,
      host,
      params: getTokenRef.current
        ? async (): Promise<Record<string, string>> => {
            const t = await getTokenRef.current!();
            return t ? { token: t } : {};
          }
        : undefined,
    });
    setAcquired({ key: noteTargetKey(args), doc: handle.doc });

    const unsubscribe = handle.doc.onChange(() => {
      forceRender((v) => v + 1);
    });
    const onUpdate = () => forceRender((v) => v + 1);
    handle.doc.doc.on('update', onUpdate);

    return () => {
      unsubscribe();
      handle.doc.doc.off('update', onUpdate);
      handle.release();
      setAcquired(null);
    };
    // host/getToken intentionally excluded — see refs above. Re-acquire
    // only when the *target* changes (party/room/deckId/noteId).
  }, [args.party, args.room, args.deckId, args.noteId]);

  if (!acquired || acquired.key !== targetKey) return EMPTY;
  const doc = acquired.doc;
  return {
    doc,
    fragment: doc.peekTiptapFragment(),
    idbSynced: doc.idbSynced,
    wsSynced: doc.wsSynced,
    synced: doc.synced,
    error: doc.lastError,
  };
}

/**
 * Personal-scope note doc. Reads party/room/host/getToken from the
 * personal `NoteDocSpawnProvider` mounted by `UserDoConnectionProvider`.
 */
export function useNoteDoc(deckId: string, noteId?: string): UseNoteDocResult {
  const cfg = useNoteDocSpawnConfig();
  return useNoteDocInternal({
    party: cfg?.party ?? null,
    room: cfg?.room ?? null,
    host: cfg?.host ?? null,
    getToken: cfg?.getToken,
    deckId,
    noteId,
  });
}

/**
 * Group-scope note doc. Routes to `study-group-do` with the groupId
 * as the room. Reuses `host` + `getToken` from the personal spawn
 * config — the auth token authenticates the user for both DOs.
 */
export function useGroupNoteDoc(
  groupId: string,
  deckId: string,
  noteId?: string,
): UseNoteDocResult {
  const cfg = useNoteDocSpawnConfig();
  return useNoteDocInternal({
    party: 'study-group-do',
    room: groupId,
    host: cfg?.host ?? null,
    getToken: cfg?.getToken,
    deckId,
    noteId,
  });
}
